import { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ErrorWrapper } from "~/components/ErrorPage";
import { MetaTagUnserialized, MetaTags } from "~/components/MetaTags";
import { NodeFull } from "~/components/NodeFull";
import { NodeFullTour } from "~/components/NodeFull/NodeFullTour";
import { NodeType, ViewMode } from "~/constants";
import { MetaTag, useRouteQuery } from "~/generated";
import { nodeHasNavMode, nodeTypeHasImage } from "~/utils";
import { transformSingleUrlToListingPreviewRoute } from "./utils";

export type NodeRouteProps = {
  type: NodeType;
  hasNavigationMode?: boolean;
  defaultMode: ViewMode;
};
export const NodeRoute = ({ type, defaultMode }: NodeRouteProps) => {
  const { pathname } = useLocation();
  const { data, loading, error } = useRouteQuery({
    variables: { path: pathname },
  });

  const routeMetaTags = useMemo<(MetaTag | MetaTagUnserialized)[]>(
    () =>
      data?.node?.name
        ? [
            {
              tag: "title",
              children: data?.node?.name + " - SiRo",
            },
          ]
        : [],
    [data?.node?.name]
  );

  if (error) {
    throw error;
  }

  // If we are not loading anymore and there is no data and no error, navigate
  // to the 404 page.
  if (!loading && (!data || !data.node)) {
    return <Navigate to="/404" replace />;
  }

  if (defaultMode === ViewMode.Nav) {
    const newRoute = transformSingleUrlToListingPreviewRoute(
      pathname,
      type,
      ViewMode.Nav
    );
    return <Navigate to={newRoute} replace />;
  }

  return (
    <ErrorWrapper>
      <MetaTags def={routeMetaTags} />
      {type === NodeType.TopicTour ? (
        <NodeFullTour
          loading={loading}
          heading={data?.node?.name}
          content={data?.node as any}
          hasTextMode={true}
          hasNavMode={nodeHasNavMode(type)}
        />
      ) : (
        <NodeFull
          loading={loading}
          heading={data?.node?.name}
          content={data?.node as any}
          hasImage={nodeTypeHasImage(type)}
          hasTaxonomies={type === NodeType.Place}
        />
      )}
    </ErrorWrapper>
  );
};
